import { z } from "zod";

export const DASHBOARD_WIDGETS = [
  { id: "summary", title: "Ключевые показатели", width: 12, height: null },
  { id: "daily", title: "Динамика по дням", width: 8, height: 300 },
  { id: "funnel", title: "Воронка по статусам", width: 4, height: 300 },
  { id: "cities", title: "Чаще встречающиеся города", width: 4, height: 380 },
  { id: "age", title: "Распределение возрастов", width: 4, height: 380 },
  { id: "gender", title: "Мужчины и женщины", width: 4, height: 380 },
  { id: "recruiters", title: "Рекрутёры", width: 8, height: 280 },
  { id: "objects", title: "Объекты работы", width: 4, height: 280 },
] as const;
export type DashboardWidgetId = (typeof DASHBOARD_WIDGETS)[number]["id"];
export type DashboardWidgetWidth = 4 | 6 | 8 | 12;
export const dashboardWidgetSchema = z
  .object({
    id: z.enum([
      "summary",
      "daily",
      "funnel",
      "cities",
      "age",
      "gender",
      "recruiters",
      "objects",
    ]),
    width: z.union([z.literal(4), z.literal(6), z.literal(8), z.literal(12)]),
    height: z.number().int().min(200).max(1000).nullable(),
    hidden: z.boolean(),
  })
  .strict()
  .refine(
    (widget) => widget.id !== "summary" || widget.height === null,
    "Высота показателей определяется содержимым",
  );
export const dashboardLayoutSchema = z
  .object({
    version: z.literal(1),
    widgets: z
      .array(dashboardWidgetSchema)
      .length(DASHBOARD_WIDGETS.length)
      .refine(
        (widgets) =>
          new Set(widgets.map((widget) => widget.id)).size ===
          DASHBOARD_WIDGETS.length,
        "Виджеты не должны повторяться",
      ),
  })
  .strict();
export type DashboardWidgetLayout = z.infer<typeof dashboardWidgetSchema>;
export type DashboardLayout = z.infer<typeof dashboardLayoutSchema>;
export function defaultDashboardLayout(): DashboardLayout {
  return {
    version: 1,
    widgets: DASHBOARD_WIDGETS.map(({ id, width, height }) => ({
      id,
      width,
      height,
      hidden: false,
    })),
  };
}
/** Некорректные или устаревшие настройки не ломают открытие отчёта. */
export function resolveDashboardLayout(raw: unknown): DashboardLayout {
  const parsed = dashboardLayoutSchema.safeParse(raw);
  return parsed.success ? parsed.data : defaultDashboardLayout();
}
export function moveDashboardWidget(
  layout: DashboardLayout,
  id: DashboardWidgetId,
  target: DashboardWidgetId,
): DashboardLayout {
  const from = layout.widgets.findIndex((widget) => widget.id === id);
  const to = layout.widgets.findIndex((widget) => widget.id === target);
  if (from < 0 || to < 0 || from === to) return layout;
  const widgets = [...layout.widgets];
  const [item] = widgets.splice(from, 1);
  widgets.splice(to, 0, item!);
  return { ...layout, widgets };
}
